import { Link } from "react-router-dom";
import { Gauge, Droplets, Disc3, BatteryCharging, ScanLine, MoveVertical, Waves, Timer, CircleDashed, Wind, Snowflake, Wrench, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { pathFor } from "@/i18n/translations";
import { IMAGES } from "@/config/images";
import { Reveal } from "@/components/Reveal";
import { PageHero, AccentButton, OutlineButton } from "@/components/ui-kit";
import { ServiceWhatsApp } from "@/components/ContactWidgets";

const ICONS = [Gauge, Droplets, Disc3, BatteryCharging, ScanLine, MoveVertical, Waves, Timer, CircleDashed, Wind, Snowflake, Wrench];

export default function Mechanical() {
  const { lang, t } = useLang();
  const m = t.mechanicalPage;
  return (
    <div>
      <PageHero image={IMAGES.mechanicHero} imagePosition="center top" alt={t.alts.mechanicalHero} overline={t.mechPreview.overline} titleLines={[m.heroTitle]} subtitle={m.heroSub} />

      {/* Diagnostics feature block */}
      <section className="py-24 md:py-32 bg-[var(--bg-secondary)]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <div className="relative overflow-hidden aspect-[16/10]">
              <img src={IMAGES.diagnostics} alt={t.alts.diagnostics} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display font-semibold text-white text-3xl sm:text-4xl">{t.mechPreview.title}</h2>
            <p className="mt-5 text-zinc-400 leading-relaxed">{t.mechPreview.intro}</p>
          </Reveal>
        </div>
      </section>

      {/* Service blocks grid */}
      <section className="py-24 md:py-32 bg-[var(--bg-primary)]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
            {m.blocks.map((b, i) => {
              const Ico = ICONS[i % ICONS.length];
              return (
                <Reveal key={i} delay={(i % 3) * 0.05}>
                  <div data-testid={`mech-block-${i}`} className="group bg-[var(--bg-secondary)] p-8 md:p-10 h-full hover:bg-[var(--bg-tertiary)] transition-colors">
                    <Ico size={28} className="text-white group-hover:text-[var(--accent)] transition-colors" strokeWidth={1.4} />
                    <h3 className="mt-6 font-display text-xl font-medium text-white">{b.name}</h3>
                    <p className="mt-2.5 text-sm text-zinc-400 leading-relaxed">{b.desc}</p>
                    <div className="mt-5"><ServiceWhatsApp service={b.name} testid={`mech-block-wa-${i}`} /></div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* End CTA */}
      <section className="on-dark py-24 md:py-28" style={{ background: "var(--accent)" }}>
        <div className="mx-auto max-w-[1400px] px-5 md:px-8 text-center">
          <h2 className="font-display font-semibold text-white text-4xl sm:text-5xl">{m.endTitle}</h2>
          <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
            <AccentBtnLight to={pathFor(lang, "contact")} testid="mech-quote-btn" label={t.cta.quote} />
            <OutlineButton to={pathFor(lang, "contact")} testid="mech-book-btn">{t.cta.book}</OutlineButton>
          </div>
        </div>
      </section>
    </div>
  );
}

// White button variant for accent background sections.
const AccentBtnLight = ({ to, testid, label }) => (
  <Link to={to} data-testid={testid} className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-[var(--accent)] bg-white px-7 py-4 hover:-translate-y-0.5 transition-transform">
    {label} <ArrowRight size={16} />
  </Link>
);
